import { t as lnT } from '../i18n';
import { isTauriEnvironment } from './tauri';

const UPDATE_CHECK_DELAY_MS = 5000;
const UPDATE_CHECK_STORAGE_KEY = 'luckynote_desktop_update_last_checked_at';
const UPDATE_CHECK_INTERVAL_MS = 12 * 60 * 60 * 1000;

let pendingUpdate = null;

const loadUpdaterApis = async () => {
    const [{ check }, { relaunch }, dialog] = await Promise.all([
        import('@tauri-apps/plugin-updater'),
        import('@tauri-apps/plugin-process'),
        import('@tauri-apps/plugin-dialog')
    ]);

    return { check, relaunch, dialog };
};

const confirmDesktopUpdate = async (version) => {
    const { dialog } = await loadUpdaterApis();
    return await dialog.ask(
        `Luckynote ${version} is available.\n\nDo you want to download and install it now? The app will restart after the update is installed.`,
        {
            title: lnT('common:updateLuckynote'),
            kind: 'info',
            okLabel: lnT('common:downloadUpdate'),
            cancelLabel: lnT('common:later')
        }
    );
};

const notifyDesktopUpdateStatus = async (message, kind = 'info') => {
    try {
        const { dialog } = await loadUpdaterApis();
        await dialog.message(message, {
            title: lnT('common:luckynoteUpdates'),
            kind
        });
    } catch (error) {
        window.alert(message);
    }
};

export const getDesktopUpdateStatus = async ({ force = false } = {}) => {
    if (!isTauriEnvironment()) {
        return { available: false, skipped: true, reason: 'not_tauri' };
    }

    if (!force) {
        const lastCheckedAt = Number(localStorage.getItem(UPDATE_CHECK_STORAGE_KEY) || 0);
        if (Date.now() - lastCheckedAt < UPDATE_CHECK_INTERVAL_MS) {
            return { available: false, skipped: true, reason: 'recently_checked' };
        }
    }

    try {
        localStorage.setItem(UPDATE_CHECK_STORAGE_KEY, String(Date.now()));

        const { check } = await loadUpdaterApis();
        const update = await check();
        if (!update) {
            pendingUpdate = null;
            return { available: false };
        }

        pendingUpdate = update;
        return {
            available: true,
            version: update.version,
            currentVersion: update.currentVersion,
            date: update.date,
            body: update.body
        };
    } catch (error) {
        console.warn('[desktop updater] Update check failed:', error);
        return { available: false, error };
    }
};

export const installDesktopUpdate = async () => {
    if (!isTauriEnvironment()) {
        return { installed: false, skipped: true, reason: 'not_tauri' };
    }

    try {
        const { check, relaunch } = await loadUpdaterApis();
        const update = pendingUpdate || await check();
        if (!update) {
            pendingUpdate = null;
            return { installed: false, available: false };
        }

        pendingUpdate = update;
        await update.downloadAndInstall();
        await relaunch();

        return { available: true, installed: true };
    } catch (error) {
        console.warn('[desktop updater] Update install failed:', error);
        return { available: true, installed: false, error };
    }
};

export const checkForDesktopUpdate = async ({ force = false, notifyWhenCurrent = false } = {}) => {
    const status = await getDesktopUpdateStatus({ force });

    if (status.error) {
        if (notifyWhenCurrent) {
            await notifyDesktopUpdateStatus(
                'Could not check for updates. Please try again in a moment.',
                'error'
            );
        }
        return status;
    }

    if (status.skipped) {
        return status;
    }

    if (!status.available) {
        if (notifyWhenCurrent) {
            await notifyDesktopUpdateStatus('Luckynote is up to date.');
        }
        return status;
    }

    try {
        const shouldInstall = await confirmDesktopUpdate(status.version);

        if (!shouldInstall) {
            return { available: true, installed: false };
        }

        return await installDesktopUpdate();
    } catch (error) {
        console.warn('[desktop updater] Update check failed:', error);
        return { available: false, error };
    }
};

export const registerDesktopUpdateMenuListener = () => {
    if (!isTauriEnvironment()) {
        return undefined;
    }

    let disposed = false;
    let unlisten = null;

    import('@tauri-apps/api/event')
        .then(({ listen }) => listen('desktop-check-for-updates', () => {
            checkForDesktopUpdate({ force: true, notifyWhenCurrent: true });
        }))
        .then((unlistenEvent) => {
            if (disposed) {
                unlistenEvent();
                return;
            }
            unlisten = unlistenEvent;
        })
        .catch((error) => {
            console.warn('[desktop updater] Could not register update menu listener:', error);
        });

    return () => {
        disposed = true;
        if (unlisten) {
            unlisten();
        }
    };
};

export const scheduleDesktopUpdateCheck = () => {
    if (!isTauriEnvironment()) {
        return undefined;
    }

    const timeoutId = window.setTimeout(() => {
        checkForDesktopUpdate();
    }, UPDATE_CHECK_DELAY_MS);

    return () => window.clearTimeout(timeoutId);
};
